import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import { getUserInfo } from './apiUtils.js';
import { ChevronDown, Search, SortAsc, SortDesc, Calendar, Filter, X, Plus,  AlertTriangle } from 'lucide-react';


// Import the JSON-based configuration
import appConfig, { ENDPOINT_KEYS } from './config/appConfig';


// Dialog component with IntegrationSystems styling
const DialogStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
  },
  dialog: {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '8px',
    width: '400px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  title: {
    fontSize: '18px',
    textAlign: 'center',
    margin: 0,
    color: '#000',
    backgroundColor: 'white',
  },
  button: {
    backgroundColor: '#FFFDD0',
    border: 'none',
    borderRadius: '4px',
    padding: '10px',
    width: '100%',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 500,
    color: 'black'
  }
};

const Dialog = ({ message, onClose, onCancel }) => {
  const isConfirmation = message.startsWith('Are you sure');
  const isEmptyItems = message.startsWith('No') && (message.includes('Categories') || message.includes('Attributes'));

  const formattedMessage = message.split('\n').map((line, i) => (
    <React.Fragment key={i}>
      {line}
      {i < message.split('\n').length - 1 && <br />}
    </React.Fragment>
  ));

  return (
    <div style={DialogStyles.overlay}>
      <div style={{
        ...DialogStyles.dialog,
        borderRadius: isEmptyItems ? '16px' : '8px',
        border: isEmptyItems ? '1px solid #ccc' : 'none',
      }}>
        <h2 style={{
          ...DialogStyles.title,
          fontSize: isEmptyItems ? '16.8px' : '18px',
          lineHeight: isEmptyItems ? '1.4' : '1.2',
        }}>
          {formattedMessage}
        </h2>
        {isConfirmation ? (
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              style={{
                ...DialogStyles.button,
                backgroundColor: '#FF8F89',
                color: 'white',
                width: '50%'
              }}
              onClick={onClose}
            >
              Yes
            </button>
            <button
              style={{
                ...DialogStyles.button,
                width: '50%'
              }}
              onClick={onCancel}
            >
              No
            </button>
          </div>
        ) : (
          <button
            style={{
              ...DialogStyles.button,
              backgroundColor: isEmptyItems ? '#FFFDD0' : '#FFFDD0',
              width: isEmptyItems ? '120px' : '100%',
              margin: isEmptyItems ? '0 auto' : '0',
              display: 'block'
            }}
            onClick={onClose}
          >
            OK
          </button>
        )}
      </div>
    </div>
  );
};






// Create/Edit Dialog component matching IntegrationSystems style
const CreateCategoryDialog = ({ isOpen, onClose, onSubmit, category, isEdit, isAttribute, selectedCategory }) => {
  const [description, setDescription] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const descriptionInputRef = useRef(null);
  const nameInputRef = useRef(null);

  useEffect(() => {
    if (category) {
      setDescription(category.category_description || '');
      setName(isAttribute ? category.attribute_name : category.attribute_category || '');
    } else {
      setDescription('');
      setName('');
    }
  }, [category, isAttribute]);

  useEffect(() => {
    if (isOpen && !isEdit) {
      setName('');
      setDescription('');
      setError('');
    } else if (isEdit) {
      setError('');
    }
  }, [isOpen, isEdit]);

  useEffect(() => {
    if (isOpen && nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const formData = isAttribute
        ? {
            attribute_name: name,
            category_description: description,
          }
        : {
            attribute_category: name,
            category_description: description
          };

      await onSubmit(formData);

      // For create mode, clear form and keep dialog open
      if (!isEdit) {
        setDescription('');
        setName('');
        nameInputRef.current?.focus();
      }
      // For edit mode, dialog will be closed by the parent handler

    } catch (err) {
      console.error('Submit error:', err);
      setError(err.message || `Failed to ${isEdit ? 'update' : 'create'} ${isAttribute ? 'attribute' : 'category'}`);
    }
  };

  const handleClose = () => {
    setDescription('');
    setName('');
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div style={DialogStyles.overlay}>
      <div style={{...DialogStyles.dialog, width: '500px'}}>
        <h2 style={{ ...DialogStyles.title, backgroundColor: '#CCCCFF' }}>
          {isEdit ? `Edit ${isAttribute ? 'Exception Value' : 'Exception Category'}` : `Create New ${isAttribute ? 'Exception Value' : 'Exception Category'}`}
        </h2>
        {isAttribute && (
          <div style={{marginBottom: '15px', fontSize: '14px', color: '#666'}}>
            Exception Category: {selectedCategory?.attribute_category}
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div style={{marginBottom: '15px'}}>
            <label style={{display: 'block', marginBottom: '5px'}}>
              {isAttribute ? 'Exception Value*:' : 'Exception Category Name*:'}
            </label>
            <input
              type="text"
              value={name}
              ref={nameInputRef}
              onChange={(e) => setName(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
              maxLength={100}
              disabled={isEdit}
            />
          </div>
          <div style={{marginBottom: '15px'}}>
            <label style={{display: 'block', marginBottom: '5px'}}>Description:</label>
            <input
              type="text"
              value={description}
              ref={descriptionInputRef}
              onChange={(e) => setDescription(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
              maxLength={300}
            />
          </div>

          {/* Error Messages - Only show errors in dialog */}
          {error && (
            <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-red-800">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium">
                    {error}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-center gap-4">
            <button
              type="button"
              onClick={handleClose}
              style={{
                backgroundColor: '#FFFDD0',
                padding: '8px 16px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Close
            </button>
            <button
              type="submit"
              style={{
                padding: '8px 16px',
                backgroundColor: '#C8F4DE',
                color: 'black',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              {isEdit ? 'Update' : `Add New ${isAttribute ? 'Exception Value' : 'Exception Category'}`}
            </button>
          </div>

          {!isEdit && (
            <p className="text-gray-600 text-sm mt-4 text-center">
              You can continue adding more {isAttribute ? 'exception values' : 'exception categories'}. Click 'Close' when finished.
            </p>
          )}
        </form>
      </div>
    </div>
  );
};

const FunctionalAttributes = () => {
  // State declarations
  const [showDialog, setShowDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [redirectPath, setRedirectPath] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categories, setCategories] = useState([]);
  const [attributes, setAttributes] = useState([]);
  const [corporateAccount, setCorporateAccount] = useState('');
  const [currentProject, setCurrentProject] = useState('');
  const [userId, setUserId] = useState('');
  const [userName, setUserName] = useState('');
  const [accountDescription, setAccountDescription] = useState('');
  const [permissions, setPermissions] = useState({
    canDelete: false,
    canEdit: false,
    canCreate: false,
    canView: true
  });

  const [selectedRows, setSelectedRows] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedCategoryForEdit, setSelectedCategoryForEdit] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const login_token = localStorage.getItem('authToken');


const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
const [itemToDelete, setItemToDelete] = useState(null);


const renderDeleteConfirmDialog = () => {
  if (!isDeleteConfirmOpen || !itemToDelete) return null;

  const isCategory = !selectedCategory;
  const itemType = isCategory ? 'category' : 'attribute';
  const itemTypePlural = isCategory ? 'categories' : 'attributes';

  return (
    <div style={{...DialogStyles.overlay, zIndex: 1100}}>
      <div style={{...DialogStyles.dialog, width: '600px'}}>
        <div style={{
          backgroundColor: '#FFE6E6',
          color: '#CC0000',
          padding: '15px 0',
          textAlign: 'center',
          fontSize: '18px',
          fontWeight: 'bold',
          borderRadius: '8px 8px 0 0',
          margin: '-20px -20px 20px -20px'
        }}>
          <div className="flex items-center justify-center gap-2">
            <AlertTriangle size={20} />
            <span>Confirm Deletion</span>
          </div>
        </div>

        <div className="px-6 py-4">
          <p className="text-gray-700 mb-4">
            {itemToDelete.id === 'multiple'
              ? `Are you sure you want to delete these ${itemTypePlural}?`
              : `Are you sure you want to delete this ${itemType}?`}
          </p>
          <p className="text-sm text-red-600 mb-4">
            This action cannot be undone.
          </p>

          {/* Show item details */}
          <div className="bg-gray-50 rounded p-3 mb-4">
            <p className="text-xs text-gray-600 mb-2">{itemType} details:</p>
            <div className="text-sm text-gray-700">
              {itemToDelete.id === 'multiple' ? (
                <div><strong>Selected Items:</strong> {itemToDelete.description}</div>
              ) : (
                <>
                  <div><strong>{isCategory ? 'Category Name' : 'Attribute Name'}:</strong> {itemToDelete.name}</div>
                  <div><strong>Description:</strong> {itemToDelete.category_description || 'No description'}</div>
                  {isCategory && <div><strong>Category:</strong> {itemToDelete.attribute_category}</div>}
                  {!isCategory && selectedCategory && (
                    <div><strong>Category:</strong> {selectedCategory.attribute_category}</div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-center gap-4 p-6 pt-0">
          <button
            onClick={() => {
              setIsDeleteConfirmOpen(false);
              setItemToDelete(null);
            }}
            style={{
              backgroundColor: '#f8f9fa',
              padding: '8px 16px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleDeleteConfirmed}
            style={{
              backgroundColor: '#dc3545',
              color: 'white',
              padding: '8px 16px',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            {itemToDelete.id === 'multiple'
              ? `Delete ${isCategory ? 'Categories' : 'Attributes'}`
              : `Delete ${isCategory ? 'Category' : 'Attribute'}`}
          </button>
        </div>
      </div>
    </div>
  );
};

  // Watch for search query changes and refresh list when cleared
  useEffect(() => {
    if (searchQuery === '') {
      // Refresh the appropriate list when search is cleared
      if (selectedCategory) {
        fetchAttributes(selectedCategory.attribute_category);
      } else {
        fetchCategories();
      }
    }
  }, [searchQuery, selectedCategory, corporateAccount, currentProject]);

  // Sorting functionality
  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Apply sorting to data
  const sortedData = React.useMemo(() => {
    const dataToSort = selectedCategory ? attributes : categories;
    let sortableData = [...dataToSort];
    if (sortConfig.key) {
      sortableData.sort((a, b) => {
        let aVal = a[sortConfig.key] || '';
        let bVal = b[sortConfig.key] || '';

        aVal = aVal.toString().toLowerCase();
        bVal = bVal.toString().toLowerCase();

        if (aVal < bVal) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aVal > bVal) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableData;
  }, [categories, attributes, selectedCategory, sortConfig]);

  const styles = {
    deleteButton: {
      backgroundColor: '#dc3545',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      padding: '6px 12px',
      height: '28px',
      fontSize: '0.8rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    editButton: {
      backgroundColor: '#4CAF50',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      padding: '6px 12px',
      height: '28px',
      fontSize: '0.8rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    disabled: {
      opacity: 0.6,
    },
    container: {
      padding: '20px',
      maxWidth: '1200px',
      margin: '0 auto',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      marginTop: '0px',
      backgroundColor: '#fff',
      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      fontSize: '0.875rem',
    },
    th: {
      backgroundColor: '#E5E4E2',
      color: '#0064e5',
      padding: '6px 12px',
      textAlign: 'left',
      borderBottom: '2px solid #ddd',
      fontSize: '0.875rem',
      lineHeight: '1',
      height: '32px',
      cursor: 'pointer',
      userSelect: 'none',
      position: 'relative'
    },
    thNonSortable: {
      backgroundColor: '#E5E4E2',
      color: '#0064e5',
      padding: '6px 12px',
      textAlign: 'left',
      borderBottom: '2px solid #ddd',
      fontSize: '0.875rem',
      lineHeight: '1',
      height: '32px'
    },
    td: {
      padding: '12px',
      borderBottom: '1px solid #ddd',
    },
    checkbox: {
      marginRight: '10px',
    },
    error: {
      color: 'red',
      padding: '10px',
      marginBottom: '10px',
    },
  };

  // Updated permission check function using new API
  const checkPermission = async (categoryHeader) => {
    try {
      const response = await fetch(appConfig.getUrl('/get_user_access_level_new'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          user_id: userId,
          project_id: currentProject,
          category_header: categoryHeader
        }),
      });

      const data = await response.json();

      // Create new permissions object - View is always true (no need to check)
      const newPermissions = {
        canView: true,
        canDelete: false,
        canEdit: false,
        canCreate: false
      };

      // Map API response to permission fields
      data.user_actions_list.forEach(item => {
        switch(item.user_action) {
          case 'Create or Edit Exception Configuration':
            newPermissions.canEdit = item.has_access;
            newPermissions.canCreate = item.has_access;
            break;
          case 'Delete Exception Configuration':
            newPermissions.canDelete = item.has_access;
            break;
        }
      });

      setPermissions(newPermissions);
      console.log('Permissions updated:', newPermissions);

      return data.access_status && data.status === 'Success';
    } catch (error) {
      console.error(`Error checking permission for ${categoryHeader}:`, error);
      return false;
    }
  };

  useEffect(() => {
    if (corporateAccount && userId && currentProject) {
      checkPermission('Exceptions Definition');
    }
  }, [corporateAccount, userId, currentProject]);

  useEffect(() => {
    if (corporateAccount && currentProject) {
      fetchCategories();
    }
  }, [corporateAccount, currentProject]);

  const showErrorDialog = (message, path) => {
    setDialogMessage(message);
    setRedirectPath(path);
    setShowDialog(true);
  };


const handleDialogClose = () => {
  setShowDialog(false);
  if (redirectPath) {
    window.location.href = redirectPath;
  }
};


const handleDialogCancel = () => {
  setShowDialog(false);
  // Remove the pendingDeleteAction logic
};

  const handleSearch = () => {
    if (selectedCategory) {
      fetchAttributes(selectedCategory.attribute_category);
    } else {
      fetchCategories();
    }
  };

  const handlePageChange = (newPage) => {
    setSelectedRows(new Set());
    setCurrentPage(newPage);
  };

  const handleCreateAttribute = async (attributeData) => {
    if (!permissions.canCreate) {
      return Promise.reject(new Error('Insufficient permissions to create attribute'));
    }

    try {
      const response = await fetch(appConfig.getUrl('/create_key_functional_attribute'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          attribute_category: selectedCategory.attribute_category,
          attribute_name: attributeData.attribute_name,
          attribute_description: attributeData.category_description
        }),
      });

      const data = await response.json();
      if (data.status === 'Success') {
        // Set success message in main component
        setSuccess('Exception value created successfully!');
        setError('');

        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(''), 3000);

        setSelectedCategoryForEdit(null);
        setSelectedRows(new Set());
        fetchAttributes(selectedCategory.attribute_category);

        return Promise.resolve();
      } else {
        return Promise.reject(new Error(data.status_description || 'Failed to create attribute'));
      }
    } catch (err) {
      setError(err.message || 'Failed to create attribute');
      setSuccess('');
      return Promise.reject(new Error(err.message || 'Failed to create attribute'));
    }
  };

  const handleEditAttribute = async (attributeData) => {
    if (!permissions.canEdit) {
      return Promise.reject(new Error('Insufficient permissions to edit attribute'));
    }

    try {
      const response = await fetch(appConfig.getUrl('/update_key_functional_attribute'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          key_attribute_list_id: selectedCategoryForEdit.key_attribute_list_id,
          corporate_account: corporateAccount,
          project_id: currentProject,
          attribute_category: selectedCategory.attribute_category,
          attribute_name: selectedCategoryForEdit.attribute_name,
          attribute_description: attributeData.category_description
        }),
      });

      const data = await response.json();
      if (data.status === 'Success') {
        // Close dialog immediately for edit operations
        setIsCreateDialogOpen(false);
        setSelectedCategoryForEdit(null);
        setIsEditMode(false);

        // Set success message in main component with delay
        setTimeout(() => {
          setSuccess('Exception value updated successfully!');
          setError('');

          // Clear success message after 3 seconds
          setTimeout(() => setSuccess(''), 3000);
        }, 100);

        setSelectedRows(new Set());
        fetchAttributes(selectedCategory.attribute_category);

        return Promise.resolve();
      } else {
        return Promise.reject(new Error(data.status_description || 'Failed to update attribute'));
      }
    } catch (err) {
      setError(err.message || 'Failed to update attribute');
      setSuccess('');
      return Promise.reject(new Error(err.message || 'Failed to update attribute'));
    }
  };

  const handleCreateCategory = async (categoryData) => {
    if (!permissions.canCreate) {
      return Promise.reject(new Error('Insufficient permissions to create category'));
    }

    try {
      const response = await fetch(appConfig.getUrl('/create_key_functional_attribute_categories'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          attribute_category: categoryData.attribute_category,
          category_description: categoryData.category_description
        }),
      });

      const data = await response.json();
      if (data.status === 'Success') {
        // Set success message in main component
        setSuccess('Exception category created successfully!');
        setError('');

        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(''), 3000);

        setSelectedRows(new Set());
        fetchCategories();

        return Promise.resolve();
      } else {
        return Promise.reject(new Error(data.status_description || 'Failed to create category'));
      }
    } catch (err) {
      setError(err.message || 'Failed to create category');
      setSuccess('');
      return Promise.reject(new Error(err.message || 'Failed to create category'));
    }
  };

  const handleEditCategory = async (categoryData) => {
    if (!permissions.canEdit) {
      return Promise.reject(new Error('Insufficient permissions to edit category'));
    }

    try {
      const response = await fetch(appConfig.getUrl('/update_key_functional_attribute_categories'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          attribute_category: selectedCategoryForEdit.attribute_category,
          category_description: categoryData.category_description
        }),
      });

      const data = await response.json();
      if (data.status === 'Success') {
        // Close dialog immediately for edit operations
        setIsCreateDialogOpen(false);
        setSelectedCategoryForEdit(null);
        setIsEditMode(false);

        // Set success message in main component with delay
        setTimeout(() => {
          setSuccess('Exception category updated successfully!');
          setError('');

          // Clear success message after 3 seconds
          setTimeout(() => setSuccess(''), 3000);
        }, 100);

        setSelectedRows(new Set());
        fetchCategories();

        return Promise.resolve();
      } else {
        return Promise.reject(new Error(data.status_description || 'Failed to update category'));
      }
    } catch (err) {
      setError(err.message || 'Failed to update category');
      setSuccess('');
      return Promise.reject(new Error(err.message || 'Failed to update category'));
    }
  };

  // Row-level edit handler
  const handleRowEdit = (item) => {
    if (permissions.canEdit) {
      setSelectedCategoryForEdit(item);
      setIsEditMode(true);
      setIsCreateDialogOpen(true);
    }
  };




 const handleDelete = async () => {
  if (!permissions.canDelete) {
    setError('Insufficient permissions to delete items');
    return;
  }

  if (selectedRows.size === 0) {
    setError('Please select at least one item to delete');
    return;
  }

  const isCategory = !selectedCategory;
  const itemType = isCategory ? 'category' : 'attribute';

  // For single selection, show detailed confirmation
  if (selectedRows.size === 1) {
    const selectedId = Array.from(selectedRows)[0];
    let item;

    if (isCategory) {
      item = categories.find(c => c.attribute_category === selectedId);
      setItemToDelete({
        id: selectedId,
        name: item?.attribute_category,
        category_description: item?.category_description,
        attribute_category: item?.attribute_category
      });
    } else {
      item = attributes.find(a => a.attribute_name === selectedId);
      setItemToDelete({
        id: selectedId,
        name: item?.attribute_name,
        category_description: item?.category_description,
        attribute_name: item?.attribute_name
      });
    }

    setIsDeleteConfirmOpen(true);
    return;
  }

  // For multiple selections, create a summary object
  const multipleItemsObject = {
    id: 'multiple',
    description: `${selectedRows.size} selected ${itemType}${selectedRows.size > 1 ? 's' : ''}`,
    actualIds: Array.from(selectedRows)
  };

  setItemToDelete(multipleItemsObject);
  setIsDeleteConfirmOpen(true);
};




const handleDeleteConfirmed = async () => {
  setIsDeleteConfirmOpen(false);
  setIsDeleting(true);
  setDeleteError(null);
  setError(null);
  setSuccess('');

  try {
    // Handle both single and multiple deletions
    const itemIds = itemToDelete.id === 'multiple'
      ? itemToDelete.actualIds
      : [itemToDelete.id];

    let apiEndpoint = '';
    let requestBody = {};

    if (!selectedCategory) {
      // Deleting categories
      apiEndpoint = appConfig.getUrl('/delete_key_functional_attribute_categories');
      requestBody = {
        corporate_account: corporateAccount,
        project_id: currentProject,
        attribute_categories: itemIds,
      };
    } else {
      // Deleting attributes
      apiEndpoint = appConfig.getUrl('/delete_key_functional_attribute');
      requestBody = {
        corporate_account: corporateAccount,
        project_id: currentProject,
        attribute_category: selectedCategory.attribute_category,
        attribute_names: itemIds
      };
    }

    const response = await fetch(apiEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${login_token}`
      },
      body: JSON.stringify(requestBody),
    });

    const data = await response.json();
    if (data.status === 'Success') {
      const message = itemIds.length === 1
        ? `${selectedCategory ? 'Attribute' : 'Category'} deleted successfully!`
        : `${selectedCategory ? 'Attributes' : 'Categories'} deleted successfully!`;
      setSuccess(message);
      setError('');

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);

      setSelectedRows(new Set());
      if (selectedCategory) {
        fetchAttributes(selectedCategory.attribute_category);
      } else {
        fetchCategories();
      }
    } else {
      throw new Error(data.status_description || 'Failed to delete items');
    }
  } catch (err) {
    setError('Failed to delete items: ' + err.message);
    setSuccess('');
  } finally {
    setIsDeleting(false);
    setItemToDelete(null);
  }
};





  // Enhanced dialog close handler that refreshes lists
  const handleCreateDialogClose = () => {
    setIsCreateDialogOpen(false);
    setSelectedCategoryForEdit(null);
    setIsEditMode(false);

    // Refresh the appropriate list when dialog closes
    if (selectedCategory) {
      fetchAttributes(selectedCategory.attribute_category);
    } else {
      fetchCategories();
    }
  };

  useEffect(() => {
    const loadInitialData = async () => {
      const current_project = localStorage.getItem('current_project');

      if (!login_token) {
        showErrorDialog('Invalid login credentials. Please login first', '/LoginAndAuthentication');
        return;
      }

      try {
        const result = await getUserInfo({
          login_token: login_token
        });

        if (result.success) {
          if (result.userInfo.default_project === 'null' && current_project === 'null') {
            showErrorDialog('Please select a project on the home page', '/RequirementsPortal');
            return;
          }

          setCorporateAccount(result.userInfo.corporate_account);
          setUserId(result.user_id);
          setUserName(result.userInfo.user_name);
          setAccountDescription(result.userInfo.account_description);
          setCurrentProject(current_project === 'null' ? result.userInfo.default_project : current_project);
        } else {
          showErrorDialog('Invalid login credentials. Please login again', '/LoginAndAuthentication');
        }
      } catch (error) {
        console.error('Error in initial data load:', error);
        setError(error.message);
      }
    };

    loadInitialData();
  }, []);

  const fetchCategories = async () => {
    if (!corporateAccount || !currentProject) return;

    setLoading(true);
    setSelectedRows(new Set());
    setCurrentPage(1);

    try {
      const response = await fetch(appConfig.getUrl('/get_key_functional_attribute_categories'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          search_query: searchQuery
        }),
      });

      const data = await response.json();
      if (data.status === 'Success') {
        setCategories(data.attribute_category_list || []);
        if (data.attribute_category_list?.length === 0) {
          setDialogMessage('No Categories found to load.\nCreate new categories');
//          setShowDialog(true);
        }
      } else {
        setCategories([]);
//        setError(data.status_description);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchAttributes = async (category) => {
    if (!corporateAccount || !currentProject) return;

    setLoading(true);
    setSelectedRows(new Set());
    setCurrentPage(1);

    try {
      const response = await fetch(appConfig.getUrl('/get_key_functional_attributes'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          attribute_category: category,
          search_query: searchQuery
        }),
      });

      const data = await response.json();
      if (data.status === 'Success') {
        setAttributes(data.attribute_list || []);
        if (data.attribute_list?.length === 0) {
          setDialogMessage('No Attributes found to load.\nCreate new attributes');
//          setShowDialog(true);
        }
      } else {
        setAttributes([]);
//        setDialogMessage(data.status_description);
//        setShowDialog(true);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryClick = (category) => {
    setCurrentPage(1);
    setAttributes([]);
    setSelectedCategory(category);
    fetchAttributes(category.attribute_category);
  };

  const toggleSelectAll = () => {
    if (selectedRows.size === paginatedData.length) {
      setSelectedRows(new Set());
    } else {
      const newSelected = new Set(paginatedData.map(item =>
        selectedCategory ? item.attribute_name : item.attribute_category
      ));
      setSelectedRows(newSelected);
    }
  };

  const toggleRowSelection = (id) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  // Pagination calculations
  const totalPages = Math.ceil((sortedData?.length || 0) / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = (sortedData || []).slice(startIndex, startIndex + itemsPerPage);

  if (loading) return <div>Loading...</div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header default_project={currentProject} />

      <div className="flex h-[calc(100vh-64px)]">
        <div className="flex-1 overflow-y-auto p-6">
          <div style={styles.container}>

            {/* Title bar */}
            <h1 className="text-l font-bold text-blue-800 mt-6 mr-20 text-center">
              Manage Exceptions Configuration
            </h1>
            <div className="flex items-center mb-4 mt-3 justify-between bg-blue-100 p-3 rounded">

              {/* Search bar */}
              <div className="flex items-center">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                  placeholder={`Search ${selectedCategory ? 'attributes' : 'categories'}...`}
                  className="p-2 pr-10 border rounded w-full"
                />
                <button
                  onClick={handleSearch}
                  className="ml-2 p-2 rounded bg-blue-500 hover:bg-blue-600 text-white"
                >
                  <Search size={18} />
                </button>
              </div>

              <h2 className="text-l font-bold text-blue-700 text-center flex-1">{currentProject}</h2>

              <div className="group relative">
                <button
                  disabled={!permissions.canCreate}
                  onClick={() => {
                    if (permissions.canCreate) {
                      setIsEditMode(false);
                      setSelectedCategoryForEdit(null);
                      setIsCreateDialogOpen(true);
                    }
                  }}
                  className={`flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 ${!permissions.canCreate ? 'opacity-50' : ''}`}
                >
                  <Plus size={18} className="mr-2" />
                  Create New
                </button>

                {/* Create button tooltip */}
                {!permissions.canCreate && (
                  <div className="absolute left-0 top-full mt-2 px-3 py-2 bg-gray-200 text-gray-800 text-xs rounded shadow-lg invisible group-hover:visible whitespace-nowrap z-20">
                    Insufficient permission
                    <div className="absolute bottom-full right-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-600"></div>
                  </div>
                )}
              </div>
            </div>

            {/* Navigation breadcrumb */}
            {selectedCategory && (
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg ">
                  Exception Values for Category: <strong>{selectedCategory.attribute_category}</strong>
                </h2>
                <button
                  className="text-blue-600 hover:text-blue-800"
                  onClick={() => {
                    setAttributes([]);
                    setSelectedCategory(null);
                    setSelectedRows(new Set());
                  }}
                >
                  ← Back to Categories
                </button>
              </div>
            )}

            {/* Main table */}
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.thNonSortable}>
                    <input
                      type="checkbox"
                      checked={paginatedData.length > 0 && selectedRows.size === paginatedData.length}
                      onChange={toggleSelectAll}
                      style={styles.checkbox}
                    />
                    Select All
                  </th>
                  <th
                    style={styles.th}
                    onClick={() => handleSort(selectedCategory ? 'attribute_name' : 'attribute_category')}
                    className="select-none hover:bg-gray-200 transition-colors"
                  >
                    <div className="flex items-center">
                      <span style={{ textDecoration: 'underline' }}>
                        {selectedCategory ? 'Exception Value' : 'Exception Category'}
                      </span>
                      <span className="ml-1">
                        {sortConfig.key === (selectedCategory ? 'attribute_name' : 'attribute_category') ? (
                          sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />
                        ) : null}
                      </span>
                    </div>
                  </th>
                  <th
                    style={styles.th}
                    onClick={() => handleSort('category_description')}
                    className="select-none hover:bg-gray-200 transition-colors"
                  >
                    <div className="flex items-center">
                      <span style={{ textDecoration: 'underline' }}>Description</span>
                      <span className="ml-1">
                        {sortConfig.key === 'category_description' ? (
                          sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />
                        ) : null}
                      </span>
                    </div>
                  </th>
                  <th style={styles.thNonSortable}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((item) => {
                  const id = selectedCategory ? item.attribute_name : item.attribute_category;
                  const name = selectedCategory ? item.attribute_name : item.attribute_category;

                  return (
                    <tr
                      key={id}
                      style={{
                        backgroundColor: selectedRows.has(id) ? '#DEEFF5' : 'transparent'
                      }}
                      className="border-t hover:bg-gray-50"
                    >
                      <td style={styles.td}>
                        <input
                          type="checkbox"
                          checked={selectedRows.has(id)}
                          onChange={() => toggleRowSelection(id)}
                          style={styles.checkbox}
                        />
                      </td>
                      <td style={styles.td}>
                        {selectedCategory ? (
                          name
                        ) : (
                          <a
                            href="#"
                            className="text-blue-600 hover:text-blue-800 hover:underline"
                            onClick={(e) => {
                              e.preventDefault();
                              handleCategoryClick(item);
                            }}
                          >
                            {name}
                          </a>
                        )}
                      </td>
                      <td style={styles.td}>{item.category_description}</td>
                      {/* NEW: Added Actions column with edit icon */}
                      <td style={styles.td}>
                        <div className="group relative">
                          <button
                            onClick={() => handleRowEdit(item)}
                            disabled={!permissions.canEdit}
                            className={`p-1 rounded hover:bg-blue-100 ${
                              !permissions.canEdit ? 'opacity-50' : 'cursor-pointer'
                            }`}
                            style={{ color: permissions.canEdit ? '#4CAF50' : '#999' }}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                              <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.828-2.828z" />
                            </svg>
                          </button>

                          {/* Edit button tooltip */}
                          {!permissions.canEdit && (
                            <div className="absolute left-0 top-full mt-2 px-3 py-2 bg-gray-200 text-gray-800 text-xs rounded shadow-lg invisible group-hover:visible whitespace-nowrap z-20">
                              Insufficient permission
                              <div className="absolute bottom-full left-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-800"></div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {sortedData.length === 0 && (
                  <tr>
                    <td colSpan="4" className="px-4 py-8 text-center text-gray-500">
                      No {selectedCategory ? 'attributes' : 'categories'} found. Create new or adjust your search.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* Pagination controls */}
            {!loading && sortedData.length > 0 && (
              <div className="mt-4 flex justify-between items-center bg-white p-4 rounded-lg shadow">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <span>Total Entries: {sortedData.length}</span>
                  <span>| Showing {sortedData.length > 0 ? startIndex + 1 : 0} - {Math.min(startIndex + itemsPerPage, sortedData.length)} of {sortedData.length}</span>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    className="p-1 rounded hover:bg-gray-200 disabled:opacity-50"
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    aria-label="Previous page"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <span className="text-sm text-blue-600">Page {currentPage} of {Math.max(1, totalPages)}</span>
                  <button
                    className="p-1 rounded hover:bg-gray-200 disabled:opacity-50"
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages || totalPages === 0}
                    aria-label="Next page"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>

                <div className="flex items-center">
                  <span className="text-sm text-gray-600 mr-2">Items per page:</span>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="border rounded p-1 text-sm"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>
              </div>
            )}

            {/* Error/Success Messages */}
            {(error || success) && (
              <div className={`mb-4 p-3 rounded-md ${
                success
                  ? 'bg-green-50 border border-green-200 text-green-800'
                  : 'bg-red-50 border border-red-200 text-red-800'
              }`}>
                <div className="flex">
                  <div className="flex-shrink-0">
                    {success ? (
                      // Success icon
                      <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      // Error icon
                      <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium">
                      {error || success}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {deleteError && (
              <div className="mb-4 p-2 rounded bg-red-50 text-red-500">
                {deleteError}
              </div>
            )}

            {/* Action buttons */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '20px',
              width: '100%'
            }}>
              <div style={{ display: 'flex', gap: '10px' }}>
                {/* Delete button with tooltip */}
                <div className="group relative">
                  <button
                    style={{
                      ...styles.deleteButton,
                      padding: '8px 16px',
                      ...(isDeleting || selectedRows.size === 0 || !permissions.canDelete ? styles.disabled : {})
                    }}
                    onClick={handleDelete}
                    disabled={isDeleting || selectedRows.size === 0 || !permissions.canDelete}
                  >
                    {isDeleting ? 'Deleting...' : `Delete Selected (${selectedRows.size})`}
                  </button>

                  {/* Delete button tooltip */}
                  {(selectedRows.size === 0 || !permissions.canDelete) && (
                    <div className="absolute left-0 top-full mt-2 px-3 py-2 bg-gray-200 text-gray-800 text-xs rounded shadow-lg invisible group-hover:visible whitespace-nowrap z-20">
                      {selectedRows.size === 0
                        ? "Select one or more items to delete"
                        : "Insufficient permission"
                      }
                      <div className="absolute bottom-full left-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-800"></div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="w-48 bg-white border-l border-gray-200">
          <div className="sticky top-[64px] bg-white z-10 p-4 border-b">
            <h3 className="font-semibold text-sm text-gray-700">Related Actions</h3>
          </div>
          <div className="p-4 space-y-2 text-sm">
            <Link to="/attributes/import" className="block text-blue-500 hover:underline">
              Import Attributes
            </Link>
            <Link to="/attributes/export" className="block text-blue-500 hover:underline">
              Export Attributes
            </Link>
          </div>
        </div>
      </div>

{showDialog && (
  <Dialog
    message={dialogMessage}
    onClose={handleDialogClose}
    onCancel={handleDialogCancel}
  />
)}

{/* Delete Confirmation Dialog */}
{renderDeleteConfirmDialog()}

      <CreateCategoryDialog
        isOpen={isCreateDialogOpen}
        onClose={handleCreateDialogClose}
        onSubmit={
          isEditMode
            ? (selectedCategory ? handleEditAttribute : handleEditCategory)
            : (selectedCategory ? handleCreateAttribute : handleCreateCategory)
        }
        category={selectedCategoryForEdit}
        isEdit={isEditMode}
        isAttribute={!!selectedCategory}
        selectedCategory={selectedCategory}
      />
    </div>
  );
};

export default FunctionalAttributes;